import React from 'react';
import { HeroImage } from '../components/HeroImage';
import { Target, Lightbulb, Layers, TrendingUp } from 'lucide-react';

export const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroImage
        title="ABOUT OPTI FLO APPS LTD"
        subtitle="We build AI-first platforms and integrations that power modern businesses, agencies, and creators."
      />

      <div className="container mx-auto px-5 md:px-10">

        {/* What We Do - White Background */}
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-8">WHAT WE DO</h2>
            <div className="bg-black border border-gray-800 rounded-xl p-8 md:p-12">
              <p className="body-large text-on-dark mb-6">
                Opti Flo Apps Ltd develops a suite of independent but interconnected platforms designed for the modern web. We focus on three core areas:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-[#00d9ff] via-[#0066ff] to-[#f43cde] text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                    1
                  </div>
                  <div>
                    <p className="body-medium text-on-dark font-semibold mb-2">Platform Infrastructure</p>
                    <p className="body-medium text-on-dark">
                      Building modern frontends and integration layers for popular platforms like GoHighLevel
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-[#00d9ff] via-[#0066ff] to-[#f43cde] text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                    2
                  </div>
                  <div>
                    <p className="body-medium text-on-dark font-semibold mb-2">Marketing Intelligence</p>
                    <p className="body-medium text-on-dark">
                      Providing AI-powered tools for SEO, link management, and visibility optimization
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-[#00d9ff] via-[#0066ff] to-[#f43cde] text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                    3
                  </div>
                  <div>
                    <p className="body-medium text-on-dark font-semibold mb-2">Experiential Commerce</p>
                    <p className="body-medium text-on-dark">
                      Creating social-first experiences that connect people with products and services
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Our Philosophy - Black Background */}
        <section className="py-20 md:py-32 bg-black">
          <div className="container mx-auto px-5 md:px-10">
            <h2 className="heading-2 text-center mb-16">OUR PHILOSOPHY</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="text-[#f43cde]">
                    <Target size={32} />
                  </div>
                </div>
                <h3 className="heading-5 mb-3">AI-First</h3>
                <p className="body-small text-on-dark text-muted-dark">
                  Every product leverages artificial intelligence to deliver smarter, faster experiences.
                </p>
              </div>

              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="text-[#f43cde]">
                    <Layers size={32} />
                  </div>
                </div>
                <h3 className="heading-5 mb-3">Modular</h3>
                <p className="body-small text-on-dark text-muted-dark">
                  Independent products that work together, giving you flexibility without vendor lock-in.
                </p>
              </div>

              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="text-[#f43cde]">
                    <Lightbulb size={32} />
                  </div>
                </div>
                <h3 className="heading-5 mb-3">Modern Frontends</h3>
                <p className="body-small text-on-dark text-muted-dark">
                  Beautiful, fast, and intuitive interfaces that users love to interact with.
                </p>
              </div>

              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="text-[#f43cde]">
                    <TrendingUp size={32} />
                  </div>
                </div>
                <h3 className="heading-5 mb-3">Ecosystem Approach</h3>
                <p className="body-small text-on-dark text-muted-dark">
                  Products designed to integrate and amplify each other's capabilities.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why We Exist - White Background */}
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-8">WHY WE EXIST</h2>
            <div className="bg-black border border-gray-800 rounded-xl p-8 md:p-12">
              <p className="body-large text-on-dark mb-6">
                The modern web needs better tools. Too many businesses are constrained by platforms that weren't built for today's requirements:
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <span className="text-[#f43cde] text-xl mt-1">•</span>
                  <p className="body-medium text-on-dark">Legacy interfaces that don't leverage modern design patterns</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#f43cde] text-xl mt-1">•</span>
                  <p className="body-medium text-on-dark">Limited integration capabilities that create data silos</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#f43cde] text-xl mt-1">•</span>
                  <p className="body-medium text-on-dark">Missing AI capabilities that could automate routine tasks</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#f43cde] text-xl mt-1">•</span>
                  <p className="body-medium text-on-dark">Monolithic systems that force unnecessary compromises</p>
                </li>
              </ul>
              <p className="body-large text-on-dark">
                We're building solutions that address these gaps while maintaining simplicity and usability.
              </p>
            </div>
          </div>
        </section>

        {/* How Products Are Structured - Black Background */}
        <section className="py-20 md:py-32 bg-black">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 text-center mb-16">HOW OUR PRODUCTS ARE STRUCTURED</h2>
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-xl p-8">
                <h3 className="heading-5 text-on-white mb-4">Independent Products</h3>
                <p className="body-medium text-on-white text-muted-white">
                  Each product stands alone with its own domain, branding, and value proposition. You can use one without needing the others.
                </p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-xl p-8">
                <h3 className="heading-5 text-on-white mb-4">Shared Backbone</h3>
                <p className="body-medium text-on-white text-muted-white">
                  Behind the scenes, products share infrastructure, authentication, and data models where it makes sense. This allows us to ship faster and maintain higher quality.
                </p>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-xl p-8">
                <h3 className="heading-5 text-on-white mb-4">Optional Integration</h3>
                <p className="body-medium text-on-white text-muted-white">
                  When you use multiple products, they can work together seamlessly. But integration is optional, never required.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Building - White Background */}
        <section className="py-20 md:py-32 bg-white">
          <div className="container mx-auto px-5 md:px-10 text-center">
            <h2 className="heading-2 mb-8">TRUSTED FOR</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto">
              <div className="bg-black border border-gray-800 rounded-xl p-6 hover:border-[#f43cde] transition-all hover:shadow-[0_0_30px_rgba(244,60,222,0.3)]">
                <h3 className="heading-5 mb-2">Platform Reviews</h3>
                <p className="body-small text-on-dark text-muted-dark">Vetted by major platforms and marketplaces</p>
              </div>
              <div className="bg-black border border-gray-800 rounded-xl p-6 hover:border-[#f43cde] transition-all hover:shadow-[0_0_30px_rgba(244,60,222,0.3)]">
                <h3 className="heading-5 mb-2">Enterprise Use</h3>
                <p className="body-small text-on-dark text-muted-dark">Deployed in production environments</p>
              </div>
              <div className="bg-black border border-gray-800 rounded-xl p-6 hover:border-[#f43cde] transition-all hover:shadow-[0_0_30px_rgba(244,60,222,0.3)]">
                <h3 className="heading-5 mb-2">Partner Integrations</h3>
                <p className="body-small text-on-dark text-muted-dark">Connected to industry-leading services</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};